<script>
  import Vue from 'vue'
  import { mapGetters } from 'vuex'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import store from '@/store/store'
  import { positivePayMixins } from '@/Mixins/PositivePay'
  import { aggregateMixins } from '@/Mixins/Aggregate'
  import { reportMixins } from '@/Mixins/Reports'
  import { AgGridVue } from 'ag-grid-vue'
  import PayrollDirectDepositList from '@/components/SigmaClientNew/PayrollDirectDepositList.vue'
  import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
  import AgGridAccountsPayableColumns from '@/Mixins/SigmaClientNew/AgGridAccountsPayableColumns'
  import { VendorResource, PayrollResource, ProjectResource, AssignmentResource, EmployeeResource, DepositAccountResource } from '@sigmacloud/sigma-client/dist/resources'
  import TypedExports from '@/resources/TypedExportsResource'
  import CustomAccountsPayableResource from '@/resources/CustomAccountsPayableResource'
  import { pad, showColumnNumbers } from '@/utils/helpers/StringsNumbers'

  export default Vue.extend({
    name: 'PayrollDdExport',
    mixins: [ positivePayMixins, aggregateMixins, reportMixins, AgGridMethods, AgGridAccountsPayableColumns ],
    components: {
      HeaderInfoText,
      PayrollDirectDepositList,
      AgGridVue
    },
    data () {
      return {
        reportOptions: {    // Options to run report.
          format: 'tbk',    // ACH Format
          previousExport: undefined  // Typed Exports
        },
        allCodes: {         // Show codes, e.g. accounts, series, locations, sets, etc.
          bank__in: []
        },
        selectedCodes: {    // User selected codes from choices of allCodes above
          bank: 'e0bc2eb5-edb7-4a22-be8d-095125b04546'
        },
        disableReportRun: false, // Disable run report button if false
        loading: false,
        // selectedOption: 'all',  // Future functionality
        prettyJson: undefined,
        reportOutputFormats: [
          // Can store a default value in Bank "postitivepay_host" property
          { value: 'tbk', label: 'Triumph Bank' }
          // { value: 'union', label: 'Union Bank' }
        ],
        transactionList: undefined,
        includedTypes: undefined, // for sigma-query version
        useSigmaQuery: true, // for sigma-query version
        previousExports: [],
        step: 1,
        iteration: 0,
        gridApi: null,
        columnApi: null,
        hideVendorAccountInfo: true,
        columnDefsNacha: undefined,
        columnDefsUpdate: undefined,
        gridOptionsNacha: undefined,
        gridOptionsUpdate: undefined,

        rowData: [],
        totalAvailable: undefined,
        totalSelected: undefined,
        selectedResources: [],
        simulatedTransactions: [],
        issueDate: undefined,
        checkDateLimit: undefined,
        exportLineHash: 0,
        exportRecordCount: 0,
        exportLineCount: 0,
        exportLineTotalDr: 0,
        exportLineTotalCr: 0,
        showJson: false,
        verbose: false
      }
    },
    methods: {
      /**
       * Performs basic validation, e.g. are required fields selected
       * @return {boolean}
       */
      checkRequiredElementFields () {
        /* Add "required" to el-select and "required-multiselect" to multiple selectors, e.g.
          Single select:
            <el-select v-model="bank" :multiple="false" placeholder="Bank Account" required>

          Multiple select:
            <el-select v-model="company" :multiple="true"  placeholder="All" required-multiselect>
        */
        let errorCount = 0
        const requiredInputFields = this.$el.querySelectorAll('[required] input')             // Works for el-select (non-multiple)/input tags
        for (const field of requiredInputFields) {
          field.classList.remove('form-error')
          if (field && !field.value) {
            field.classList.add('form-error')
            errorCount++
          }
        }
        const requiredMultiSelectFields = this.$el.querySelectorAll('[required-multiselect]') // Need this for multiple-select el-select boxes
        for (const field of requiredMultiSelectFields) {
          field.querySelector('input').classList.remove('form-error')
          if (field && field.querySelectorAll('.el-select__tags-text').length === 0) {
            field.querySelector('input').classList.add('form-error')
            errorCount++
          }
        }
        return !errorCount
      },

      selectPreviousExport (event) {
        this.reportOptions.previousExport = event.attributes.id
      },

      /**
       * Finds matching checks and APs to export, and then get thte data to format into the proper formats.
       * @return {Promise<boolean>}
       */
      async getTransactions () {
        if (!this.checkRequiredElementFields()) {
          return false
        }

        // this.loading = true
        this.disableReportRun = true
        if (this.reportOptions.previousExport) {
          try {
            //  const response = await this.resources.TypedExports.list({ query: { type: PR_NACHA_DD, page_size: 10, page: 1 } })
            const response = await this.resources.TypedExports.detail(this.reportOptions.previousExport)
            // AP transactions are where DDs come from
            let apIds = response.attributes.accountspayable || []
            this.transactionList = await this.resources.AccountsPayableResource.all({ query: { id__in: apIds.join(',') } }) || []
          } catch (error) {
            console.log(error)
            return error
          }
        } else {
          try {
            // Get Check/AP Records
            if (this.useSigmaQuery === true) {
              this.transactionList = await this.getExportableDirectDepositsSQ(this.selectedCodes.bank, this.checkDateLimit) // sigma-query verion
            } else {
              this.transactionList = await this.getExportableDirectDeposits(this.selectedCodes.bank, this.checkDateLimit) // sigma-api version
            }
          } catch (error) {
            console.log(error)
            return error
          }
        }

        if (!this.transactionList || this.transactionList.length === 0) {
          this.$notify({ title: 'No New Transactions', message: `There are no new payroll DD entries.`, type: 'warning' })
          this.disableReportRun = false
          return false
        }

        // Get related in bulk
        let payrollIds = []
        let assignmentIds = []
        let employeeIds = []
        let depositIds = []
        this.transactionList.forEach((ap) => {
          payrollIds.push(ap.attributes.payroll)
          assignmentIds.push(ap.attributes.assignment)
          employeeIds.push(ap.attributes.employee)
          depositIds.push(ap.attributes.deposit_account)
        })

        const payrollResources = await PayrollResource.all({ query: { id__in: payrollIds.join(',') } })
        await AssignmentResource.all({ query: { id__in: assignmentIds.join(',') } })
        await EmployeeResource.all({ query: { id__in: employeeIds.join(',') } })
        await DepositAccountResource.all({ query: { id__in: depositIds.join(',') } })
        await ProjectResource.all({ query: { id__in: payrollResources.map((pr) => pr.attributes.project).join(',') } })
        await this.createPayrollDirectDepositList() // this.transactionList, payrollResources, assignmentResources, employeeResources, depositAccountResources, projectResources)

        this.iteration++
        // this.step = 2
        this.disableReportRun = false
      },

      async createPayrollDirectDepositList () {
        if (!this.transactionList || this.transactionList.length === 0) return
        let results = []
        for (const i in this.transactionList) {
          const trans = this.transactionList[i]
          await trans.resolveRelated({ managers: [ 'payroll', 'payroll.project', 'employee', 'deposit_account' ] })
          await trans.get('payroll').resolveRelated({ managers: [ 'project' ] })
          const row = {
            project_name: trans.get('payroll.project.name'),
            payroll_id: trans.attributes.payroll,
            payroll_number: trans.get('payroll.number'),
            time_posted: trans.get('payroll.time_posted'),
            ap_id: trans.attributes.id,
            assignment_id: trans.attributes.assignment,
            employee_id: trans.attributes.employee,
            check_date: trans.get('payroll.check_date'),
            identifier: pad(trans.get('payroll.number').slice(-10), ' ', 10, 'right') + pad(trans.attributes.employee, '0', 5, 'right'),
            amount_decimal: trans.attributes.amount_decimal,
            payee: trans.get('employee.is_loanout') ? trans.get('employee.loanout_name') : trans.get('employee.last_name') + ', ' + trans.get('employee.first_name'),
            dd_name: trans.get('deposit_account.name'),
            account_number: trans.get('deposit_account.account_number'),
            routing_number: trans.get('deposit_account.routing_number'),
            is_savings_account: trans.get('deposit_account.is_savings_account')
            // count: ,
          }
          results.push(row)
        }

        this.rowData = results
        return results
      },

      async createNachaFile () {
        if (!this.rowData || this.rowData.length === 0) {
          this.$notify({ title: 'No Transactions', message: `There are no payroll DD entries to export.`, type: 'warning' })
          return false
        }
        this.loading = true
        this.exportRecordCount = 0
        this.exportLineCount = 0
        this.exportLineHash = 0
        this.exportLineTotalDr = 0
        this.exportLineTotalCr = 0
        const detailLines = this.createNachaDetailLines() // Run first to get all lines and totals
        const headerLine = this.createNachaHeaderLine()
        const batchHeaderLine = this.createNachaBatchHeaderLine()
        const batchFooterLine = this.createNachaBatchFooterLine()
        this.exportRecordCount++ // to account for footer record that we need after the calculcations
        // Figure out total rows because we need to pad lines so that the file lines are a multiple of 10.
        let rowsNeeded = 10 - (this.exportRecordCount % 10)
        this.exportRecordCount += rowsNeeded
        const footerLine = this.createNachaFooterLine() // Create last as we needed totals
        let txtfile = ''
        if (this.verbose) txtfile += showColumnNumbers()
        txtfile += headerLine
        txtfile += batchHeaderLine
        txtfile += detailLines
        txtfile += batchFooterLine
        txtfile += footerLine
        if (this.verbose) txtfile += showColumnNumbers()
        // Add fillers
        if (rowsNeeded > 0) {
          for (let i = 0; i < rowsNeeded; i++) {
            txtfile += pad('9', '9', 94, 'right') + '\n' // intentionally blank
          }
        }
        this.prettyJson = txtfile
        this.loading = false
        this.downloadCsv(`DD`, 'txt')
        return txtfile
      },
      createNachaHeaderLine () {
        const now = new Date()
        let txtfile = ''
        txtfile += '1' // Fixed 1
        txtfile += '01' // Fixed 01
        txtfile += pad((this.selectedBank.routing_number || '').replace(/\D/g, ''), ' ', 10, 'right') // Our bank routing number
        txtfile += pad((this.selectedBank.account_number || '').replace(/\D/g, ''), ' ', 10, 'right') // Our bank account
        txtfile += new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split('T')[0].replace(/\D/g, '').slice(-6) // issue date in YYMMDD format. Must be tomorrow if greater than 10am PT
        txtfile += pad(now.getHours(), 0, 2) + pad(now.getMinutes(), 0, 2) // time in HHMM format
        txtfile += 'A' // hard coded
        txtfile += '094' // hard coded
        txtfile += '10' // hard coded
        txtfile += '1' // hard coded
        txtfile += pad((this.selectedBank.name || 'UNKNOWN BANK NAME'), ' ', 23, 'left') // bank name
        txtfile += pad((this.selectedBank.name || 'UNKNOWN BANK NAME'), ' ', 23, 'left') // origin name
        txtfile += pad('', ' ', 8, 'left') + '\n' // intentionally blank
        this.exportRecordCount++
        return txtfile
      },
      createNachaBatchHeaderLine () {
        let txtfile = ''
        txtfile += '5' // Fixed 5
        txtfile += '200' // Fixed 200
        txtfile += pad('Revolution PR', ' ', 16, 'left') // Our name
        txtfile += pad('', ' ', 20, 'left') // intentionally blank
        txtfile += pad((this.selectedBank.branch_number || '').replace(/\D/g, ''), ' ', 10, 'left') // Our bank account
        txtfile += 'PPD' // hard coded PPD for DD
        txtfile += pad('PAYROLL', ' ', 10, 'left') // Our business description/unit
        txtfile += (this.issueDate || '').replace(/\D/g, '').slice(-6) // issue date in YYMMDD format. Must be tomorrow if greater than 10am PT
        txtfile += (this.issueDate || '').replace(/\D/g, '').slice(-6) // issue date in YYMMDD format. Must be tomorrow if greater than 10am PT
        txtfile += '   ' // hard coded
        txtfile += '1' // hard coded
        txtfile += pad((this.selectedBank.routing_number || '').replace(/\D/g, ''), ' ', 8, 'right').slice(-8) // Our bank routing number
        txtfile += '0000001' + '\n'
        this.exportRecordCount++
        return txtfile
      },
      createNachaFooterLine () {
        let txtfile = ''
        txtfile += '9' // Fixed 9
        txtfile += pad('1', '0', 6, 'right') // batch count
        txtfile += pad(this.exportRecordCount / 10, '0', 6, 'right') // block count
        txtfile += pad(this.exportLineCount, '0', 8, 'right') // count
        txtfile += pad(String(this.exportLineHash), '0', 10, 'right').substr(0, 10) // hash
        txtfile += pad((this.exportLineTotalDr || 0).toFixed(2).replace(/\D/g, ''), '0', 12)
        txtfile += pad((this.exportLineTotalCr || 0).toFixed(2).replace(/\D/g, ''), '0', 12)
        txtfile += pad('', ' ', 39, 'left') + '\n' // intentionally blank
        // this.exportRecordCount++ // we already counted this one
        return txtfile
      },
      createNachaBatchFooterLine () {
        let txtfile = ''
        txtfile += '8' // Fixed 8
        txtfile += '200' // Fixed 200
        txtfile += pad(this.exportLineCount, '0', 6, 'right') // count
        txtfile += pad(String(this.exportLineHash), '0', 10, 'right').substr(0, 10) // hash
        txtfile += pad((this.exportLineTotalDr || 0).toFixed(2).replace(/\D/g, ''), '0', 12)
        txtfile += pad((this.exportLineTotalCr || 0).toFixed(2).replace(/\D/g, ''), '0', 12)
        txtfile += pad((this.selectedBank.branch_number || '').replace(/\D/g, ''), ' ', 10, 'left') // Our bank account
        txtfile += pad('', ' ', 19) // blank
        txtfile += pad('', ' ', 6) // blank
        txtfile += pad((this.selectedBank.routing_number || '').replace(/\D/g, ''), ' ', 8, 'right').slice(-8) // Our bank routing number
        txtfile += '0000001' + '\n'
        this.exportRecordCount++
        return txtfile
      },
      createNachaDetailLines () {
        // =IF(LEFT(A2,1)="6",CONCAT(A2,C2,LEFT(B2 & REPT(" ",17),17),TEXT(D2, REPT("0",10)),F2,G2,H2,I2),IF(ISBLANK(#REF!),"",#REF!))
        if (!this.rowData || this.rowData.length === 0) {
          this.$notify({ title: 'No Transactions', message: `There are no payroll DD entries to export.`, type: 'warning' })
          return false
        }

        this.disableReportRun = false
        let txtfile = ''
        for (const i in this.rowData) {
          const ap = this.rowData[i]
          if (parseFloat(ap.amount_decimal) < 0) {
            // this.exportLineTotalDr += parseFloat(ap.amount_decimal) * -1
            continue // Per Mona in dd-files Slack channel, voids/refunds are handled separately
          } else {
            this.exportLineTotalCr += parseFloat(ap.amount_decimal)
          }
          this.exportLineCount++
          this.exportRecordCount++
          this.exportLineHash += parseInt((String(ap.routing_number).substring(0, 8) || 0).replace(/\D/g, ''))
          const amount = (typeof ap.amount_decimal === 'number' ? ap.amount_decimal.toFixed(2) : ap.amount_decimal) || '0'
          txtfile += '6' // Fixed 6
          txtfile += (ap.is_savings_account === true) ? '32' : '22'
          txtfile += (ap.routing_number || '').replace(/\D/g, '')
          txtfile += pad((ap.account_number || '').replace(/\D/g, ''), ' ', 17, 'left')
          txtfile += pad((amount).replace(/\D/g, ''), '0', 10)
          txtfile += pad((ap.identifier || ''), ' ', 15, 'right')
          txtfile += pad((ap.payee || ' '), ' ', 22, 'left')
          txtfile += '  ' // discretionary data
          txtfile += '0'
          txtfile += pad((this.selectedBank.routing_number || '').replace(/\D/g, ''), ' ', 8, 'right').slice(-8) // Our bank routing number
          txtfile += pad((this.exportLineCount), '0', 7) + '\n'
        }

        return txtfile
      },

      goToConfirmationPage () {
        this.simulatedTransactions = this.$refs['nacha_transactions_list'].simulatedTransactionData
        this.selectedResources = this.$refs['nacha_transactions_list'].gridApi.getSelectedRows()
        if (this.simulatedTransactions.length === 0) {
          this.createNotification('Error', 'Please select transactions to export.', 'warning', null, null, null, 5000)
          this.simulatedTransactions = undefined
          return false
        } else {
          // this.iteration++
          this.step = 3
          return true
        }
      },

      async onGenerateExport () {
        if (!this.rowData.length) {
          this.createNotification('Error', 'Please select transactions to export.', 'warning', null, null, null, 5000)
          return false
        }

        this.$refs['payroll_transactions_list'].exportToExcel()
      },

      async onPostTransactions () {
        if (!this.transactionList || !this.transactionList.length) {
          this.createNotification('Error', 'Please select transactions to export.', 'warning', null, null, null, 5000)
          return false
        }

        this.disableReportRun = true
        const apIds = this.transactionList.map((ap) => ap.id)
        let typedExport = new TypedExports({ bank: this.selectedCodes.bank, accountspayable: apIds, type: 'PR_NACHA_DD' })
        await typedExport.save()
        this.disableReportRun = false
      },

      resetPage () {
        this.step = 1
        this.rowData = []
        this.totalAvailable = undefined
        this.totalSelected = undefined
        this.selectedResources = []
        this.simulatedTransactions = []
      },

      onUpdateTotals (event) {
        this.totalAvailable = event.totalAvailable
        this.totalSelected = event.totalSelected
      },

      updateFormat () {
        if (this.selectedCodes.bank) {
          this.reportOptions.format = this.selectedBank.positivepay_host || null
        }
      }
    },
    computed: {
      ...mapGetters({
        getReportFormFields: 'getReportFormFields',
        getBankById: 'getBankById',
        getVendorById: 'getVendorById'
      }),
      resources () {
        return {
          AccountsPayableResource: CustomAccountsPayableResource,
          PayrollResource,
          ProjectResource,
          AssignmentResource,
          EmployeeResource,
          DepositAccountResource,
          VendorResource,
          TypedExports
        }
      },
      width () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return '100%'
      },
      height () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.63 + 'px'
      },
      selectedBank () {
        if (!this.selectedCodes.bank) {
          return null
        } else {
          const bank = this.getBankById(this.selectedCodes.bank)
          return bank
        }
      }
    },

    async created () {
      this.loading = true
      let response = await store.dispatch('fetchBanks')
      if (response.banks && typeof response.banks === 'object') {
        for (const i in response.banks) {
          this.allCodes.bank__in.push(response.banks[i])
        }
        if (this.allCodes.bank__in.length === 1) {
          this.selectedCodes.bank = (this.allCodes.bank__in[0] || {}).id || null
          this.updateFormat()
        }
      } else {
        throw new Error('Unable to fetch report options')
      }

      const now = new Date()
      const daysToAdd = () => {
        const morningDeadline = 10
        const dailyDeadline = 16
        const dayOfWeek = now.getDay()
        const hour = now.getHours()
        // Friday after cutoff
        if (dayOfWeek === 5 && hour > dailyDeadline) {
          return 3
        } else if (dayOfWeek === 6) {
          return 2
        } else if (dayOfWeek === 7 || hour >= morningDeadline) {
          return 1
        } else {
          return 0
        }
      }

      this.issueDate = this.checkDateLimit = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd()).toISOString().split('T')[0]
      this.loading = false
      this.disableReportRun = false
      const exportsResources = await this.resources.TypedExports.list({ query: { type: 'PR_NACHA_DD', page_size: 10 } })
      this.previousExports = exportsResources.resources || []
    }
  })
</script>

<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading...">
    <el-row type="flex" align="middle" justify="end" style="border-bottom: 1px solid lightgrey;">
      <HeaderInfoText :colspan="20" title="Payroll Direct Deposit Export" />
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <el-tag v-if="disableReportRun" type="primary">
            <i class="el-icon-loading" style="margin: 0 6px;" />Running
          </el-tag>
          <el-button v-if="rowData.length && !this.reportOptions.previousExport" type="danger" @click="onPostTransactions" :disabled="disableReportRun" style="margin: 0 15px;">Mark as Exported</el-button>
          <el-button v-if="rowData.length" type="warning" @click="createNachaFile" :disabled="disableReportRun" style="margin: 0 15px;">Create DD File</el-button>
          <el-button v-if="rowData.length" type="success" @click="onGenerateExport" :disabled="disableReportRun" style="margin: 0 15px;">Save</el-button>
          <el-button v-if="step === 1" plain type="primary" @click="getTransactions" :disabled="disableReportRun" style="margin: 0 15px;">Get <span v-if="reportOptions.previousExport">Exported</span><span v-else>Available</span> Transactions</el-button>
          <el-button v-else-if="step === 2" type="primary" @click="goToConfirmationPage" :disabled="disableReportRun" style="margin: 0 15px;">Go to Confirmation Page</el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" justify="end" class="headerRow">
      <el-col>
        <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
          Export Options
        </el-row>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <p><sup>*</sup> Denotes a required field</p>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle">
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            <div style="display: inline-flex">
              Bank Account<sup>*</sup>
            </div>
          </el-col>
          <el-col :span="9">
            <el-select v-model="selectedCodes.bank" :multiple="false" placeholder="Bank Account" @change="updateFormat" required>
              <el-option
                v-for="opt in allCodes.bank__in"
                :key="opt.id"
                :label="opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
        <!-- <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Format<sup>*</sup>
          </el-col>
          <el-col :span="9">
            <el-select v-model="reportOptions.format" required>
              <el-option
                v-for="opt in reportOutputFormats"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row> -->
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Previous Export
          </el-col>
          <el-col :span="9">
            <el-input v-model="reportOptions.previousExport" placeholder=""/>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Issue Date
          </el-col>
          <el-col :span="9">
            <el-input v-model="issueDate"/>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Check Date Limit
          </el-col>
          <el-col :span="9">
            <el-input v-model="checkDateLimit"/>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Use Sigma-Query
          </el-col>
          <el-col :span="9">
            <el-checkbox v-model="useSigmaQuery"/>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <!-- <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Summary
          </el-col>
          <el-col :span="9">
            Amount Available to Export: <br /><strong>{{ intlNumberFormatter(totalAvailable || 0) }}</strong>
          </el-col>
          <el-col :span="9">
            Amount Selected to Export: <br /><strong>{{ intlNumberFormatter(totalSelected || 0) }}</strong>
          </el-col>
        </el-row> -->
        <!-- Later Functionality
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Data
          </el-col>
          <el-col :span="18">
            <el-radio-group v-model="selectedOption">
              <el-radio-button label="all">New Checks</el-radio-button>
              <el-radio-button label="payment_type" v-if="Object.keys(includeOptions).length">Re-Export</el-radio-button>
            </el-radio-group>
          </el-col>
        </el-row>

        <el-row
          type="flex"
          align="middle"
          justify="start"
          class="headerRow"
          v-if="selectedOption === 'payment_type' && Object.keys(includeOptions).length">
          <el-col :span="24">
            <el-row type="flex" align="middle">
              <el-col :span="6">
                Payment Type
              </el-col>
              <el-col :span="18">
                <el-row type="flex" align="middle" justify="start" v-for="(option, key) in includeOptions" :key="key">
                  <el-checkbox v-model="selectedCodes[key]" />
                  <span style="margin: 6px;">{{ option.name }}</span>
                </el-row>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
        -->
      </el-col>
      <el-col :span="10">
        <el-table
          :data="previousExports"
          @row-click="selectPreviousExport"
          :default-sort = "{prop: 'timestamp', order: 'descending'}"
          ref="typed-exports-table"
          width="90%"
          :height="200"
          >
          <el-table-column
            prop="id"
            label="Id"
            align="center"
            :formatter="lookupType"
          />
          <el-table-column
            prop="attributes.accountspayable.length"
            label="Items"
            align="center"
          />
          <el-table-column
            prop="attributes.timestamp"
            label="Timestamp"
            align="center"
          />
        </el-table>
      </el-col>
    </el-row>
    <el-row class="m-top-20">
      <div v-if="step === 3">
        <span class="m-left-10 bold red">Please confirm that the information below is correct.  If you create an export, these transactions will be marked as processed.</span>
      </div>
      <div v-else-if="step === 4">
        <span class="m-left-10 bold blue">Please download your file and upload to the bank.  Afterward, please update the payment confirmation numbers below with the bank's transaction numbers.</span>
      </div>
    </el-row>
    <el-row v-if="showJson" class="m-top-50">
      <el-col>
        <textarea id="output" style="width: 100%; height: 500px;" v-model="prettyJson"></textarea>
      </el-col>
    </el-row>
    <el-row class="m-top-20">
      <el-col>
        <payroll-direct-deposit-list
          v-if="step < 3"
          name="payroll_transactions_list"
          ref="payroll_transactions_list"
          :key="iteration"
          :width="width"
          :height="height"
          :row-data="rowData"
          @update-totals="onUpdateTotals"
        />
      </el-col>
    </el-row>
  </div>
</template>

<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-right-20 { margin-right: 20px; }
  .m-left-10 { margin-left: 10px; }
  .m-left-20 { margin-left: 20px; }
  .m-top-20 { margin-top: 20px; }
  .m-top-50 { margin-top: 50px; }
  .date {
    display: inline-flex;
  }
  .field {
    margin: 0 6px;
  }
  textarea {
    font: 10px;
    font-family: monospace;
  }
</style>
<style>
  .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  .form-error {background-color: #F08080; font-weight: bold; color: #000000; }
  /* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0,0,0,.12),0 0 6px rgba(0,0,0,.04); display: inline-block; width: 30%; position: relative; height: 500px; */
</style>



// WEBPACK FOOTER //
// src/components/Payroll/PayrollDdExport.vue